import { useEffect, useState, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2, Search, LogOut, Download, X, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import AdminCalls from "./AdminCalls";
import AdminLessonVideos from "./AdminLessonVideos";
import AdminDrip from "./AdminDrip";
import AdminPurge from "./AdminPurge";
import AdminLaunch from "./AdminLaunch";
import AdminMembers from "./AdminMembers";
import AdminWorkspaces from "./AdminWorkspaces";
import { useAuth } from "../context/AuthContext";
import { StatusPill } from "../components/site/Reveal";

const STATUS_ORDER = ["New", "Reviewed", "Contacted", "Qualified", "Accepted Pending Payment", "Enrolled", "Follow Up Later", "Not a Fit"];

const RoadmapList = ({ title, items, danger = false }) => (
  <div className="mt-4">
    <p className={`font-mono text-[10px] uppercase tracking-[0.2em] ${danger ? "text-safety" : "text-slate-500"}`}>{title}</p>
    <ul className="mt-2 space-y-1.5">
      {items.map((item) => (
        <li key={item} className={`text-sm leading-relaxed flex gap-2 ${danger ? "text-slate-400" : "text-slate-300"}`}>
          <span className={`shrink-0 mt-1.5 h-1 w-1 ${danger ? "bg-safety" : "bg-slate-500"}`} />
          {item}
        </li>
      ))}
    </ul>
  </div>
);

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [apps, setApps] = useState([]);
  const [statuses, setStatuses] = useState(STATUS_ORDER);
  const [tab, setTab] = useState("pipeline");
  const [filter, setFilter] = useState("");
  const [viewMode, setViewMode] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [contacts, setContacts] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [newCard, setNewCard] = useState({ code: "", name: "", note: "" });

  const addRoadmapCard = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/roadmap", newCard);
      toast.success("Private card added");
      setNewCard({ code: "", name: "", note: "" });
      const { data } = await api.get("/admin/roadmap");
      setRoadmap(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Could not add card"); }
  };

  const deleteRoadmapCard = async (code) => {
    try {
      await api.delete(`/admin/roadmap/${code}`);
      toast.success(`Card ${code} removed`);
      const { data } = await api.get("/admin/roadmap");
      setRoadmap(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Could not remove card"); }
  };
  const [hubContent, setHubContent] = useState(null);
  const [replyDrafts, setReplyDrafts] = useState({});
  const [roadmap, setRoadmap] = useState(null);
  const [settingsForm, setSettingsForm] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, a] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/applications", { params: { status: filter || undefined, q: q || undefined } }),
      ]);
      setStats(s.data);
      setApps(a.data.applications);
      setStatuses(a.data.statuses);
    } catch {
      toast.error("Failed to load admin data");
    }
  }, [filter, q]);

  useEffect(() => { if (user?.role === "admin") load(); }, [user, load]);

  useEffect(() => {
    if (user?.role !== "admin") return;
    if (tab === "contacts") api.get("/admin/contacts").then((r) => setContacts(r.data.contacts)).catch(() => {});
    if (tab === "help") {
      api.get("/admin/help-requests").then((r) => setTickets(r.data.tickets)).catch(() => {});
      api.get("/hub/content").then((r) => setHubContent(r.data)).catch(() => {});
    }
    if (tab === "roadmap") api.get("/admin/roadmap").then((r) => setRoadmap(r.data)).catch(() => {});
    if (tab === "settings") api.get("/admin/settings").then((r) => setSettingsForm(r.data)).catch(() => {});
  }, [tab, user]);

  const saveSettings = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      const { data } = await api.patch("/admin/settings", settingsForm);
      setSettingsForm(data);
      toast.success("Settings saved");
      if (data.admin_login_email !== user.email) {
        toast.info(`Admin login email changed to ${data.admin_login_email} — use it next sign-in.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const updateStatus = async (appId, status) => {
    try {
      const { data } = await api.patch(`/admin/applications/${appId}`, { status, note });
      toast.success(`Moved to "${status}"`);
      setSelected(data);
      setNote("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const exportCsv = () => {
    const rows = [["Name", "Email", "Business", "Trade", "Team", "Revenue", "Status", "Score", "Applied"].join(",")];
    apps.forEach((a) => rows.push([a.name, a.email, a.business_name, a.trade, a.team_size, a.revenue_tier, a.status, a.score, a.created_at.slice(0, 10)].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")));
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const el = document.createElement("a");
    el.href = URL.createObjectURL(blob);
    el.download = "workbase-applications.csv";
    el.click();
    URL.revokeObjectURL(el.href);
  };

  if (loading) return <div className="min-h-screen bg-navy grid place-items-center"><Loader2 className="text-safety animate-spin" size={36} /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/hub" replace />;

  const TABS = [
    { id: "pipeline", label: "Lead Pipeline" },
    { id: "members", label: "Members" },
    { id: "contacts", label: "Contact Inbox" },
    { id: "help", label: "Member SOS" },
    { id: "calls", label: "Calls & Replays" },
    { id: "videos", label: "Lesson Videos" },
    { id: "drip", label: "Drip Schedule" },
    { id: "testdata", label: "Test Data" },
    { id: "launch", label: "Launch Checklist" },
    { id: "workspaces", label: "Workspaces" },
    { id: "settings", label: "Settings" },
    { id: "roadmap", label: "Future (Private)" },
  ];

  const ticketLocation = (t) => {
    if (!t.module) return "";
    const m = hubContent?.modules.find((x) => x.id === t.module || x.code === String(t.module).toUpperCase());
    if (!m) return t.lesson ? `${t.module} · Lesson ${t.lesson}` : t.module;
    const l = t.lesson ? m.lessons.find((x) => x.id === t.lesson) : null;
    const lessonPart = l ? ` · ${l.title}` : t.lesson ? ` · Lesson ${String(t.lesson).replace("l", "")}` : "";
    return `${m.code} — ${m.title}${lessonPart}`;
  };

  const sendReply = async (ticketId) => {
    const body = (replyDrafts[ticketId] || "").trim();
    if (body.length < 2) return;
    try {
      await api.post(`/admin/help-requests/${ticketId}/reply`, { body });
      toast.success("Reply sent — the member sees it in their Hub");
      setReplyDrafts({ ...replyDrafts, [ticketId]: "" });
      api.get("/admin/help-requests").then((r) => setTickets(r.data.tickets)).catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reply failed");
    }
  };

  const visibleApps = apps.filter((a) => (viewMode === "all" ? true : viewMode === "test" ? !!a.is_test : !a.is_test));

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-navy pt-16">
      <div className="border-b border-white/10 bg-charcoal/60">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-5 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-safety">[ADMIN // COMMAND]</p>
            <h1 className="font-display font-extrabold text-white text-2xl mt-1">WorkBase Command</h1>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={exportCsv} data-testid="admin-export-btn"
              className="flex items-center gap-2 text-sm text-slate-300 hover:text-white border border-white/15 hover:border-safety px-4 py-2 transition-colors">
              <Download size={14} /> CSV
            </button>
            <button onClick={() => { logout(); navigate("/"); }} data-testid="admin-logout-btn"
              className="flex items-center gap-2 text-sm text-slate-500 hover:text-white px-3 py-2 transition-colors">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                tab === t.id ? "border-safety text-safety" : "border-transparent text-slate-400 hover:text-white"
              }`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        {tab === "pipeline" && (
          <>
            {stats && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-8" data-testid="admin-stats">
                {[
                  { label: "New this week", val: stats.new_this_week },
                  { label: "In pipeline", val: Object.values(stats.by_status).reduce((a, b) => a + b, 0) },
                  { label: "Active members", val: stats.active_members },
                  { label: "Collected", val: `$${stats.collected.toLocaleString()}` },
                ].map((s) => (
                  <div key={s.label} className="bg-charcoal p-5">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{s.label}</p>
                    <p className="font-display font-extrabold text-white text-3xl mt-2">{s.val}</p>
                  </div>
                ))}
              </div>
            )}
            {stats && stats.excluded_test > 0 && (
              <p className="text-slate-500 text-xs -mt-5 mb-8" data-testid="stats-test-note">
                Metrics exclude {stats.excluded_test} test record{stats.excluded_test === 1 ? "" : "s"} — test data never counts toward pipeline, members, or revenue.
              </p>
            )}

            <div className="flex flex-wrap items-center gap-2 mb-5">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mr-1">View:</span>
              {[
                { id: "all", label: `All records (${apps.length})` },
                { id: "real", label: `Real only (${apps.filter((a) => !a.is_test).length})` },
                { id: "test", label: `Test only (${apps.filter((a) => a.is_test).length})` },
              ].map((v) => (
                <button key={v.id} data-testid={`view-${v.id}`} onClick={() => setViewMode(v.id)}
                  className={`font-mono text-[11px] uppercase tracking-wider px-3 py-1.5 border transition-colors ${viewMode === v.id ? "border-violet-400 text-violet-300 bg-violet-400/10" : "border-white/15 text-slate-400 hover:border-white/40"}`}>
                  {v.label}
                </button>
              ))}
              <span className="hidden sm:inline text-slate-600 text-xs ml-2">status:</span>
              <button data-testid="admin-filter-all" onClick={() => setFilter("")}
                className={`font-mono text-[11px] uppercase tracking-wider px-3 py-1.5 border transition-colors ${!filter ? "border-safety text-safety bg-safety/10" : "border-white/15 text-slate-400"}`}>
                All
              </button>
              {statuses.map((s) => (
                <button key={s} data-testid={`admin-filter-${s.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setFilter(s === filter ? "" : s)}
                  className={`font-mono text-[11px] uppercase tracking-wider px-3 py-1.5 border transition-colors ${filter === s ? "border-safety text-safety bg-safety/10" : "border-white/15 text-slate-400 hover:border-white/40"}`}>
                  {s} {stats?.by_status?.[s] ? `(${stats.by_status[s]})` : ""}
                </button>
              ))}
              <div className="relative ml-auto">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
                  data-testid="admin-search-input" placeholder="Search name, email, business…"
                  className="bg-charcoal border border-white/15 text-white pl-9 pr-4 py-2 text-sm focus:border-safety outline-none w-64" />
              </div>
            </div>

            <div className="border border-white/10 overflow-x-auto" data-testid="admin-applications-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-charcoal text-left">
                    {["Applicant", "Trade", "Team", "Revenue", "Score", "Status", "Applied"].map((h) => (
                      <th key={h} className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 px-4 py-3">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleApps.map((a) => (
                    <tr key={a.application_id} data-testid={`admin-app-row-${a.application_id.slice(0, 8)}`}
                      onClick={() => { setSelected(a); setNote(""); }}
                      className="border-t border-white/5 hover:bg-white/5 cursor-pointer transition-colors">
                      <td className="px-4 py-3.5">
                        <p className="text-white font-medium flex items-center gap-2 flex-wrap">
                          {a.name}
                          {a.is_test && (
                            <span className="font-mono text-[9px] uppercase tracking-widest text-violet-300 border border-violet-400/40 bg-violet-400/10 px-1.5 py-0.5"
                              data-testid={`test-badge-${a.application_id.slice(0, 8)}`}>test</span>
                          )}
                        </p>
                        <p className="text-slate-500 text-xs">{a.business_name}</p>
                      </td>
                      <td className="px-4 py-3.5 text-slate-300">{a.trade}</td>
                      <td className="px-4 py-3.5 text-slate-400">{a.team_size}</td>
                      <td className="px-4 py-3.5 text-slate-400">{a.revenue_tier}</td>
                      <td className="px-4 py-3.5"><span className="font-mono text-safety">{a.score}</span></td>
                      <td className="px-4 py-3.5"><StatusPill status={a.status} /></td>
                      <td className="px-4 py-3.5 text-slate-500 font-mono text-xs">{a.created_at.slice(0, 10)}</td>
                    </tr>
                  ))}
                  {visibleApps.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">No applications match.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === "contacts" && (
          <div className="space-y-px border border-white/10" data-testid="admin-contacts">
            {contacts.length === 0 && <p className="bg-charcoal p-8 text-slate-500 text-sm">No contact messages yet.</p>}
            {contacts.map((c) => (
              <div key={c.contact_id} className="bg-charcoal p-5">
                <div className="flex justify-between gap-4">
                  <p className="text-white font-medium">{c.name} <span className="text-slate-500 font-normal">— {c.email}</span></p>
                  <p className="font-mono text-[10px] text-slate-500">{c.created_at.slice(0, 10)}</p>
                </div>
                <p className="text-slate-400 text-sm mt-2 leading-relaxed">{c.message}</p>
              </div>
            ))}
          </div>
        )}

        {tab === "help" && (
          <div className="space-y-px border border-white/10" data-testid="admin-help-tickets">
            {tickets.length === 0 && <p className="bg-charcoal p-8 text-slate-500 text-sm">No member help requests.</p>}
            {tickets.map((t) => {
              const tid = t.ticket_id.slice(0, 8);
              const answered = t.status === "Answered";
              return (
              <div key={t.ticket_id} className="bg-charcoal p-5" data-testid={`ticket-${tid}`}>
                <div className="flex justify-between gap-4 items-center">
                  <p className="text-white font-medium">{t.subject}</p>
                  <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 border ${
                    answered ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/10" : "text-amber-300 border-amber-400/30 bg-amber-400/10"
                  }`} data-testid={`ticket-status-${tid}`}>{t.status}</span>
                </div>
                <p className="font-mono text-[10px] text-slate-500 mt-1">{t.email} {ticketLocation(t) ? `— ${ticketLocation(t)}` : ""} — {t.created_at.slice(0, 10)}</p>
                <p className="text-slate-400 text-sm mt-2 leading-relaxed">{t.message}</p>
                {t.stuck_point && (
                  <p className="text-slate-400 text-xs mt-2"><span className="font-mono text-[10px] uppercase tracking-widest text-safety">Stuck on:</span> {t.stuck_point}</p>
                )}
                {t.tried && (
                  <p className="text-slate-500 text-xs mt-1.5 leading-relaxed"><span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Tried:</span> {t.tried}</p>
                )}
                {t.screenshot && (
                  <button
                    onClick={async () => {
                      try {
                        const r = await api.get(`/hub/help/${t.ticket_id}/screenshot`, { responseType: "blob" });
                        window.open(URL.createObjectURL(r.data), "_blank");
                      } catch { toast.error("Could not load screenshot"); }
                    }}
                    data-testid={`ticket-screenshot-${tid}`}
                    className="mt-3 inline-flex items-center gap-2 text-xs font-semibold text-safety hover:text-white border border-white/15 hover:border-safety px-3 py-1.5 transition-colors">
                    View screenshot
                  </button>
                )}
                {(t.replies || []).length > 0 && (
                  <div className="mt-3 space-y-2.5 border-l-2 border-white/10 pl-3" data-testid={`ticket-replies-${tid}`}>
                    {t.replies.map((r) => (
                      <div key={r.reply_id}>
                        <p className={`font-mono text-[10px] uppercase tracking-widest ${r.author_role === "admin" ? "text-safety" : "text-slate-500"}`}>
                          {r.author_role === "admin" ? "You (Coach)" : "Member"} — {r.created_at.slice(0, 10)}
                        </p>
                        <p className="text-slate-300 text-sm mt-1 leading-relaxed">{r.body}</p>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-3 flex gap-2">
                  <input value={replyDrafts[t.ticket_id] || ""} data-testid={`admin-reply-input-${tid}`}
                    onChange={(e) => setReplyDrafts({ ...replyDrafts, [t.ticket_id]: e.target.value })}
                    placeholder="Reply to member — they'll see this in their Hub…"
                    className="flex-1 bg-navy border border-white/15 text-white px-3 py-2 text-sm focus:border-safety outline-none placeholder:text-slate-600" />
                  <button onClick={() => sendReply(t.ticket_id)} data-testid={`admin-reply-send-${tid}`}
                    className="bg-safety hover:bg-safety-hover text-white text-sm font-semibold px-4 py-2 transition-colors">
                    Send
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        )}

        {tab === "calls" && <AdminCalls />}

        {tab === "members" && <AdminMembers />}

        {tab === "videos" && <AdminLessonVideos />}

        {tab === "drip" && <AdminDrip />}

        {tab === "testdata" && <AdminPurge />}

        {tab === "launch" && <AdminLaunch />}
        {tab === "workspaces" && <AdminWorkspaces />}

        {tab === "settings" && (
          <div data-testid="admin-settings" className="max-w-3xl">
            {!settingsForm ? (
              <p className="text-slate-500 text-sm">Loading settings…</p>
            ) : (
              <form onSubmit={saveSettings} className="space-y-6" data-testid="settings-form">
                <div className="bg-charcoal border border-white/10 p-6">
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-safety mb-5">[ACCESS & NOTIFICATIONS]</p>
                  <div className="grid sm:grid-cols-2 gap-5">
                    {[
                      { key: "admin_login_email", label: "Admin login email", id: "admin-login-email" },
                      { key: "application_alert_email", label: "Application-alert email", id: "application-alert-email" },
                      { key: "support_email", label: "Support / SOS-ticket email", id: "support-email" },
                      { key: "public_contact_email", label: "Public contact/support email", id: "public-contact-email" },
                      { key: "reply_to_email", label: "Reply-to email for automated messages", id: "reply-to-email" },
                      { key: "stripe_notify_email", label: "Stripe payment notification email", id: "stripe-notify-email" },
                    ].map((f) => (
                      <div key={f.key}>
                        <label className="block text-slate-400 text-xs font-mono uppercase tracking-wider mb-2">{f.label}</label>
                        <input
                          type={f.key.includes("email") ? "email" : "text"}
                          data-testid={`settings-${f.id}`}
                          value={settingsForm[f.key] || ""}
                          onChange={(e) => setSettingsForm({ ...settingsForm, [f.key]: e.target.value })}
                          required={["admin_login_email", "application_alert_email", "support_email", "stripe_notify_email"].includes(f.key)}
                          className="w-full bg-navy border border-white/15 text-white px-3.5 py-2.5 text-sm focus:border-safety outline-none"
                        />
                      </div>
                    ))}
                  </div>
                  <p className="text-slate-500 text-xs mt-4 leading-relaxed">
                    The admin login email is never shown publicly. Switch to branded business emails here once your domain is connected.
                  </p>
                </div>
                <div className="bg-charcoal border border-white/10 p-6">
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-safety mb-5">[BUSINESS & PAYMENTS]</p>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-slate-400 text-xs font-mono uppercase tracking-wider mb-2">Business name</label>
                      <input data-testid="settings-business-name" value={settingsForm.business_name || ""}
                        onChange={(e) => setSettingsForm({ ...settingsForm, business_name: e.target.value })}
                        className="w-full bg-navy border border-white/15 text-white px-3.5 py-2.5 text-sm focus:border-safety outline-none" />
                    </div>
                    <div>
                      <label className="block text-slate-400 text-xs font-mono uppercase tracking-wider mb-2">Website domain</label>
                      <input data-testid="settings-website-domain" value={settingsForm.website_domain || ""}
                        onChange={(e) => setSettingsForm({ ...settingsForm, website_domain: e.target.value })}
                        className="w-full bg-navy border border-white/15 text-white px-3.5 py-2.5 text-sm focus:border-safety outline-none" />
                      <p className="text-slate-500 text-xs mt-2 leading-relaxed">
                        Domain connection happens at deploy: Publish → Manage Publishes → Domain tab (Auto-link or manual DNS).
                        Once your domain is live, paste it here so every email link uses it. Currently in preview/test mode.
                      </p>
                    </div>
                  </div>
                  <label className="flex items-start gap-3 mt-6 cursor-pointer" data-testid="settings-live-mode-row">
                    <input type="checkbox" data-testid="settings-live-mode-toggle"
                      checked={!!settingsForm.stripe_live_mode}
                      onChange={(e) => setSettingsForm({ ...settingsForm, stripe_live_mode: e.target.checked })}
                      className="mt-0.5 accent-[#FF5500]" />
                    <span>
                      <span className="text-white text-sm font-medium">Stripe live-mode activation</span>
                      <span className="block text-slate-500 text-xs mt-1">
                        Currently: <span className="text-amber-300 font-mono">TEST MODE</span>. Checkout stays in test mode until live
                        Stripe keys are connected on deployment — this flag records your intent to go live.
                      </span>
                    </span>
                  </label>
                </div>
                <div className="bg-charcoal border border-white/10 p-6">
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-safety mb-2">[EXPLAINER VIDEO // HOMEPAGE SECTION]</p>
                  <p className="text-slate-400 text-xs mb-5 leading-relaxed">
                    Powers the "What Is WorkBase Systems?" section on the homepage. Supports YouTube, Vimeo, Loom, or a direct
                    video file URL. Until a real video is set, visitors see the branded fallback poster. The narration script
                    is pre-loaded in the transcript field — use it in ToonBee, Canva, CapCut, Synthesia, or HeyGen, then paste the link here.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-slate-400 text-xs font-mono uppercase tracking-wider mb-2">Video URL</label>
                      <input type="url" data-testid="settings-explainer-video" value={settingsForm.explainer_video_url || ""}
                        onChange={(e) => setSettingsForm({ ...settingsForm, explainer_video_url: e.target.value })}
                        placeholder="https://youtube.com/watch?v=… / loom.com/share/…"
                        className="w-full bg-navy border border-white/15 text-white px-3.5 py-2.5 text-sm focus:border-safety outline-none" />
                    </div>
                    <div>
                      <label className="block text-slate-400 text-xs font-mono uppercase tracking-wider mb-2">Custom thumbnail URL (optional)</label>
                      <input type="url" data-testid="settings-explainer-thumbnail" value={settingsForm.explainer_thumbnail_url || ""}
                        onChange={(e) => setSettingsForm({ ...settingsForm, explainer_thumbnail_url: e.target.value })}
                        placeholder="https://…"
                        className="w-full bg-navy border border-white/15 text-white px-3.5 py-2.5 text-sm focus:border-safety outline-none" />
                    </div>
                    <div>
                      <label className="block text-slate-400 text-xs font-mono uppercase tracking-wider mb-2">Captions file URL (.vtt, optional)</label>
                      <input type="url" data-testid="settings-explainer-captions" value={settingsForm.explainer_captions_url || ""}
                        onChange={(e) => setSettingsForm({ ...settingsForm, explainer_captions_url: e.target.value })}
                        placeholder="https://…/captions.vtt"
                        className="w-full bg-navy border border-white/15 text-white px-3.5 py-2.5 text-sm focus:border-safety outline-none" />
                    </div>
                    <div className="flex items-end pb-1">
                      <label className="flex items-start gap-3 cursor-pointer" data-testid="settings-explainer-autoplay-row">
                        <input type="checkbox" data-testid="settings-explainer-autoplay"
                          checked={!!settingsForm.explainer_autoplay}
                          onChange={(e) => setSettingsForm({ ...settingsForm, explainer_autoplay: e.target.checked })}
                          className="mt-0.5 accent-[#FF5500]" />
                        <span>
                          <span className="text-white text-sm font-medium">Muted autoplay</span>
                          <span className="block text-slate-500 text-xs mt-0.5">Accessible, muted, inline — plays without sound on load</span>
                        </span>
                      </label>
                    </div>
                  </div>
                  <div className="mt-5">
                    <label className="block text-slate-400 text-xs font-mono uppercase tracking-wider mb-2">Transcript (shown under the video)</label>
                    <textarea rows={5} data-testid="settings-explainer-transcript" value={settingsForm.explainer_transcript || ""}
                      onChange={(e) => setSettingsForm({ ...settingsForm, explainer_transcript: e.target.value })}
                      className="w-full bg-navy border border-white/15 text-white px-3.5 py-2.5 text-sm focus:border-safety outline-none resize-y" />
                  </div>
                </div>

                <button type="submit" disabled={savingSettings} data-testid="settings-save-btn"
                  className="bg-safety hover:bg-safety-hover disabled:opacity-50 text-white font-semibold px-8 py-3.5 transition-colors">
                  {savingSettings ? "Saving…" : "Save settings"}
                </button>
              </form>
            )}
          </div>
        )}

        {tab === "roadmap" && roadmap && (
          <div data-testid="admin-roadmap">
            <div className="bg-safety/10 border border-safety/30 p-4 mb-6 flex items-start gap-3">
              <ShieldCheck size={18} className="text-safety shrink-0 mt-0.5" />
              <p className="text-sm text-slate-300 leading-relaxed">
                Private — admin eyes only. These never appear in public navigation or marketing pages.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
              {roadmap.placeholders.map((p) => (
                <div key={p.code} className={`bg-charcoal p-6 ${p.purpose ? "sm:col-span-2" : ""}`} data-testid={`roadmap-${p.code.toLowerCase()}`}>
                  <div className="flex items-start justify-between gap-3">
                    <p className="font-mono text-[11px] tracking-[0.2em] text-slate-500">[{p.code}]</p>
                    <button data-testid={`roadmap-delete-${p.code.toLowerCase()}`} onClick={() => deleteRoadmapCard(p.code)}
                      className="text-slate-600 hover:text-red-400 transition-colors" title="Remove card"><X size={14} /></button>
                  </div>
                  <h3 className="font-display font-bold text-white mt-2">{p.name}</h3>
                  {p.note && <p className="text-slate-400 text-sm mt-2">{p.note}</p>}
                  {p.purpose && <p className="text-slate-300 text-sm mt-3 leading-relaxed">{p.purpose}</p>}
                  {p.positioning && <p className="text-slate-400 text-sm mt-2 italic leading-relaxed">{p.positioning}</p>}
                  {p.sections && (
                    <div className="mt-4 space-y-3">
                      {p.sections.map((s) => (
                        <div key={s.title} className="border-l-2 border-white/15 pl-3">
                          <p className="text-sm font-semibold text-white">{s.title}</p>
                          <p className="text-slate-400 text-sm leading-relaxed">{s.text}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {p.tenants && (
                    <div className="mt-4">
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Future tenant workspaces</p>
                      <div className="mt-2 space-y-3">
                        {p.tenants.map((t) => (
                          <div key={t.name} className="border-l-2 border-white/15 pl-3">
                            <p className="text-sm font-semibold text-white">{t.name}</p>
                            <p className="text-slate-400 text-sm leading-relaxed">{t.note}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {p.scope && <RoadmapList title="Future planning-only scope may include" items={p.scope} />}
                  {p.isolation && <RoadmapList title="Tenant isolation rules" items={p.isolation} />}
                  {p.licensing && <RoadmapList title="Future licensing may support" items={p.licensing} />}
                  {p.never && <RoadmapList title="Will never include" items={p.never} danger />}
                  {p.disclaimer && (
                    <div className="mt-4 bg-safety/10 border border-safety/30 p-3 flex items-start gap-2">
                      <ShieldCheck size={15} className="text-safety shrink-0 mt-0.5" />
                      <p className="text-xs text-slate-300 leading-relaxed">{p.disclaimer}</p>
                    </div>
                  )}
                  <span className="inline-block mt-4 font-mono text-[10px] uppercase tracking-widest text-slate-500 border border-white/15 px-2 py-1">Placeholder — not built</span>
                </div>
              ))}
            </div>
            <div className="mt-6 bg-charcoal border border-white/10 p-6">
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-2">[AI GOVERNANCE POLICY]</p>
              <p className="text-slate-300 text-sm leading-relaxed">{roadmap.ai_policy}</p>
            </div>
            <form onSubmit={addRoadmapCard} className="mt-6 bg-charcoal border border-white/10 p-6 space-y-4 max-w-xl" data-testid="roadmap-add-form">
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">Add private card</p>
              <input data-testid="roadmap-add-code" value={newCard.code} onChange={(e) => setNewCard({ ...newCard, code: e.target.value })}
                placeholder="Code (e.g. WB-NEW)" className="w-full bg-navy border border-white/15 text-white px-3.5 py-2.5 text-sm focus:border-safety outline-none" required minLength={2} maxLength={24} />
              <input data-testid="roadmap-add-name" value={newCard.name} onChange={(e) => setNewCard({ ...newCard, name: e.target.value })}
                placeholder="Name" className="w-full bg-navy border border-white/15 text-white px-3.5 py-2.5 text-sm focus:border-safety outline-none" required minLength={2} maxLength={120} />
              <textarea data-testid="roadmap-add-note" value={newCard.note} onChange={(e) => setNewCard({ ...newCard, note: e.target.value })}
                placeholder="Private note" rows={2} className="w-full bg-navy border border-white/15 text-white px-3.5 py-2.5 text-sm focus:border-safety outline-none resize-y" />
              <button data-testid="roadmap-add-submit" className="bg-safety hover:bg-safety-hover text-white text-sm font-semibold px-5 py-2.5 transition-colors">Add card</button>
              <p className="text-slate-500 text-xs">New cards are always private and marked “Placeholder — not built” until their functionality is genuinely implemented and tested.</p>
            </form>
          </div>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="admin-detail-drawer">
          <div className="absolute inset-0 bg-navy/70 backdrop-blur-sm" onClick={() => setSelected(null)} />
          <div className="relative w-full max-w-lg bg-charcoal border-l border-white/10 h-full overflow-y-auto dark-scroll p-7">
            <div className="flex justify-between items-start">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-safety">[APPLICANT DOSSIER]</p>
                <h2 className="font-display font-extrabold text-white text-2xl mt-2">{selected.name}</h2>
                <p className="text-slate-400 text-sm">{selected.business_name}</p>
              </div>
              <button onClick={() => setSelected(null)} data-testid="admin-detail-close" className="text-slate-400 hover:text-white p-1"><X size={20} /></button>
            </div>
            <div className="mt-6"><StatusPill status={selected.status} /></div>
            <dl className="mt-6 grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
              {[
                ["Email", selected.email], ["Phone", selected.phone], ["Trade", selected.trade],
                ["Team", selected.team_size], ["Revenue", selected.revenue_tier],
                ["Fit score", `${selected.score}/100`],
              ].map(([k, v]) => (
                <div key={k}>
                  <dt className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{k}</dt>
                  <dd className="text-white mt-1 break-words">{v}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-6">
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">Friction points</p>
              <div className="flex flex-wrap gap-2">
                {selected.friction_points.map((f) => (
                  <span key={f} className="text-xs bg-navy border border-white/10 text-slate-300 px-2.5 py-1">{f}</span>
                ))}
              </div>
            </div>
            {selected.software_stack && (
              <div className="mt-4">
                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">Current stack</p>
                <p className="text-slate-300 text-sm">{selected.software_stack}</p>
              </div>
            )}
            {selected.notes && (
              <div className="mt-4">
                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">Their note</p>
                <p className="text-slate-300 text-sm leading-relaxed">{selected.notes}</p>
              </div>
            )}
            <div className="mt-8 pt-6 border-t border-white/10">
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-3">Move to stage</p>
              <div className="grid grid-cols-2 gap-2">
                {statuses.map((s) => (
                  <button key={s} data-testid={`admin-set-status-${s.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => updateStatus(selected.application_id, s)}
                    disabled={s === selected.status}
                    className={`text-left text-xs font-medium px-3 py-2.5 border transition-colors ${
                      s === selected.status ? "border-safety/40 text-safety bg-safety/5 cursor-default" : "border-white/15 text-slate-300 hover:border-safety hover:text-white"
                    }`}>
                    {s}
                  </button>
                ))}
              </div>
              <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="admin-note-input"
                placeholder="Optional note for the history log…"
                className="w-full mt-3 bg-navy border border-white/15 text-white px-3 py-2.5 text-sm focus:border-safety outline-none" />
            </div>
            <div className="mt-8">
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-3">History</p>
              <div className="space-y-3">
                {[...selected.status_history].reverse().map((h, i) => (
                  <div key={i} className="flex gap-3 text-xs">
                    <span className="font-mono text-slate-600 shrink-0 w-32">{new Date(h.at).toLocaleString()}</span>
                    <div>
                      <span className="text-safety font-medium">{h.status}</span>
                      {h.note && <span className="text-slate-400"> — {h.note}</span>}
                      {h.by && <span className="text-slate-600"> ({h.by})</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
